/**
 * Below the fold: one row per integration — its system, its name, how often it moves data (⏱ daily 18:00 ·
 * ⚡ ≈ 42 runs a day), when it last ran, and what is due or wrong (next due, late, silent, failing). Words
 * are sans; only the numbers, times and ages in them are mono. A row opens the integration on the stage.
 */
import type { KeyboardEvent } from 'react';
import { Chip, Kicker, SystemBadge, cx } from '../design/primitives.tsx';
import { count } from '../lib/format.ts';
import { age } from '../lib/freshness.ts';
import { words, type RhythmRow } from './model.ts';

/** A phrase with its numbers, times and durations set in mono. */
function Words({ text }: { text: string }) {
  return <>{words(text).map((p, i) => (typeof p === 'string' ? p : <span key={i} className="now-num">{p.mono}</span>))}</>;
}

export function Rhythms({ rows, now, onRow }: { rows: RhythmRow[]; now: number; onRow: (process: string) => void }) {
  if (rows.length === 0) return null;
  const keyActivate = (process: string) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRow(process);
    }
  };
  return (
    <section className="w-full px-6 pt-12 pb-20">
      <Kicker className="px-3 pb-3">Integrations · {count(rows.length)}</Kicker>
      <div className="flex flex-col">
        {rows.map((r) => (
          <div key={r.process} role="button" tabIndex={0} className="now-rhythm-row" onClick={() => onRow(r.process)} onKeyDown={keyActivate(r.process)}>
            <SystemBadge code={r.system} size={28} />
            <span className="text-body font-medium text-n10 truncate">{r.name}</span>
            <Chip className="justify-self-start"><Words text={r.chip} /></Chip>
            <span className="text-small text-n8 whitespace-nowrap">{r.last ? <Words text={`ran ${age(r.last, now)} ago`} /> : '—'}</span>
            <span className={cx('text-small truncate', r.status?.tone === 'fail' ? 'text-fail' : r.status?.tone === 'warn' ? 'text-warn' : 'text-n8')}>
              {r.status && <Words text={r.status.text} />}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
